<template>
  <custom-popup
    :cons="cons"
    :loading.sync="loading"
    hide-confirm
    max-width="1244"
    v-model="internalIsOpen"
    persistent
    hide-overlay
    transition="dialog-bottom-transition"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @close="goto('qr/list')"
  >
    <v-card class="rounded-xl overflow-hidden d-flex flex-column" elevation="0" height="100%">
      <transition name="fade" mode="out-in">
        <!-- حالت اول: لیست بخش‌ها -->
        <div
          v-if="!selectedSection"
          key="list-view"
          class="d-flex flex-column flex-grow-1 bg-light-theme pa-2 pa-md-8 pb-0 pb-md-0"
        >
          <div class="d-flex justify-space-between align-center mb-6 flex-wrap">
            <v-card
              v-ripple
              class="rounded-xl hover-action-btn d-inline-flex align-center px-4 px-sm-8 py-4 py-sm-11 mb-2"
              elevation="7"
              @click="goToEditAll"
            >
              <v-icon color="#fb8c00" size="32" :class="$vuetify.rtl ? 'ml-2' : 'mr-2'">
                mdi-layers-edit
              </v-icon>
              <span class="text-subtitle-1 font-weight-bold" style="color: #37474f;">
                {{ $t('gateway.integrated_edit_all_sections') }}
              </span>
              <v-icon color="#90a4ae" size="15" :class="$vuetify.rtl ? 'mr-4' : 'ml-4'">
                {{ $vuetify.rtl ? 'mdi-arrow-left' : 'mdi-arrow-right' }}
              </v-icon>
            </v-card>

            <v-switch
              v-model="showDetails"
              :label="$t('gateway.show_descriptions')"
              color="orange darken-1"
              hide-details
              class="mt-0"
              inset
            />
          </div>

          <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            :label="$t('gateway.search_section_placeholder')"
            outlined
            dense
            clearable
            class="mb-5 modern-search"
            hide-details
            color="orange darken-1"
          />

          <v-card-text class="pa-0 flex-grow-1 overflow-y-auto modern-scrollbar">
            <v-row class="px-2 pb-8 m-0">
              <v-col
                v-for="(item, index) in filteredSections"
                :key="index"
                :cols="showDetails ? '12' : '6'"
                :sm="showDetails ? '6' : '4'"
                :md="showDetails ? '3' : '2'"
              >
                <InfoCard
                  :title="item.title"
                  :description="item.description"
                  :iconClass="item.icon ? 'mdi-' + item.icon : 'mdi-puzzle-outline'"
                  :themeColor="getThemeColor(index)"
                  :viewDetail="showDetails"
                  @click.native="editSingleSection(item)"
                  class="clickable-card pt-3"
                />
              </v-col>

              <v-col cols="12" v-if="filteredSections.length === 0">
                <div class="text-center d-flex flex-column align-center py-12">
                  <v-avatar color="grey lighten-4" size="80" class="mb-4">
                    <v-icon size="40" color="grey darken-1">mdi-file-search-outline</v-icon>
                  </v-avatar>
                  <span class="text-subtitle-1 grey--text text--darken-1 font-weight-medium">
                    {{ $t('gateway.no_section_found') }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </div>

        <!-- حالت دوم: فرم ویرایش تکی به همراه پیش‌نمایش -->
        <div
          v-else
          key="edit-view"
          class="d-flex flex-column bg-white"
          style="height: 100%; max-height: 75vh;"
        >
          <v-card-title class="d-flex align-center pt-2 pb-2 px-4 border-bottom flex-shrink-0">
            <v-btn
              icon
              @click="selectedSection = null"
              :class="$vuetify.rtl ? 'mr-2' : 'ml-2'"
              :title="$t('gateway.back_to_menu')"
            >
              <v-icon>{{ $vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
            </v-btn>
            <span class="text-h6 font-weight-bold ml-2">
              {{ $t('gateway.edit') }}: {{ selectedSection.title }}
            </span>
            <v-spacer />
          </v-card-title>

          <v-card-text class="px-6 pb-0 pt-0 flex-grow-1" style="overflow-y: auto;">
            <v-row class="fill-height">
              <!-- ستون پیش‌نمایش -->
              <v-col cols="12" md="4">
                <div class="sticky-preview grey lighten-4 rounded-lg pa-2">
                  <slot name="preview"></slot>
                </div>
              </v-col>

              <!-- ستون ویرایشگر و دکمه‌ها -->
              <v-col cols="12" md="8" class="d-flex flex-column relative-col">
                <SingleGalleryProvider v-if="shouldProvideSingleGallery">
                  <list-content-editor
                    v-model="internalData"
                    :components="[selectedSection]"
                    :data="internalData"
                  />
                </SingleGalleryProvider>
                <list-content-editor
                  v-else
                  v-model="internalData"
                  :components="[selectedSection]"
                  :data="internalData"
                  class="pb-16 flex-grow-1"
                />

                <div class="sticky-actions d-flex justify-space-between mt-4 pa-3 rounded-lg">
                  <v-btn class="btn-delete rounded-lg" @click="selectedSection = null">
                    <v-icon :left="$vuetify.rtl" :right="!$vuetify.rtl">mdi-close</v-icon>
                    {{ $t('gateway.cancel') }}
                  </v-btn>
                  <v-btn class="rounded-lg btn-get-code" @click="saveSingleEdit">
                    <v-icon :left="$vuetify.rtl" :right="!$vuetify.rtl">mdi-content-save</v-icon>
                    {{ $t('gateway.save') }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </transition>
    </v-card>
  </custom-popup>
</template>

<script>
import ListContentEditor from '../../../content/fragments/ListContentEditor.vue';
import InfoCard from './InfoCard.vue';
import ThemeSelector2 from '../../otherQrTypes/menu/cm/ThemeSelector2.vue';

export default {
  name: "QrEditGateway",
  components: {
    ListContentEditor,
    ThemeSelector2,
    InfoCard,
    SingleGalleryProvider: {
      provide: { provideSingleGallery: true },
      render(h) {
        return h('div', { class: 'flex-grow-1 pb-16' }, this.$slots.default);
      }
    }
  },
  props: {
    value: { type: Boolean, default: false },
    sections: { type: Array, required: true },
    formData: { type: Object, required: true },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      cons: {
        title: this.$t('gateway.edit'),
        icon: 'pencil'
      },
      searchQuery: '',
      selectedSection: null,
      internalData: this.formData,
      showDetails: false,
      themeColors: [
        '#FF6B6B', '#4facfe', '#43e97b', '#fa709a', '#a18cd1', '#0ba360',
        '#fccb90', '#00b4db', '#ffb347', '#F5295C', '#fc4a1a', '#0052D4',
        '#2193b0', '#17ead9'
      ]
    };
  },
  computed: {
    shouldProvideSingleGallery() {
      const validSections = ['VideoGallery', 'imageUploader', 'colorPallete'];
      const validRoutes = [
        'qr.edit', 'qr.name.edit', 'qr.name.id.edit', 'qr.cardVisitPro',
        'qr.name.edit.pro', 'qr.name.id.edit.pro', 'qr.memories',
        'qr.name.id.memories', 'qr.name.memories'
      ];
      return validSections.includes(this.selectedSection?.name) && validRoutes.includes(this.$route.name);
    },
    internalIsOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
        if (!val) {
          setTimeout(() => {
            this.searchQuery = '';
            this.selectedSection = null;
          }, 300);
        }
      }
    },
    filteredSections() {
      if (!this.searchQuery) return this.sections;
      const query = this.searchQuery.toLowerCase();
      return this.sections.filter(section =>
        (section.title && section.title.toLowerCase().includes(query)) ||
        (section.description && section.description.toLowerCase().includes(query))
      );
    }
  },
  watch: {
    formData: {
      deep: true,
      handler(newVal) {
        this.internalData = newVal;
      }
    },
    '$i18n.locale': {
      handler() {
        this.cons.title = this.$t('gateway.edit');
      },
      immediate: true
    }
  },
  methods: {
    getThemeColor(index) {
      return this.themeColors[index % this.themeColors.length];
    },
    closeDialog() {
      this.internalIsOpen = false;
      this.$emit('cancel');
    },
    editSingleSection(section) {
      this.selectedSection = section;
    },
    goToEditAll() {
      this.internalIsOpen = false;
      let newQuery = { ...this.$route.query };
      delete newQuery.gateway;
      this.$router.replace({ query: newQuery }).catch(() => {});
      this.$emit('edit-all');
    },
    saveSingleEdit() {
      this.$emit('save', this.internalData);
      this.selectedSection = null;
    }
  }
};
</script>

<style scoped>
.bg-light-theme {
  background-color: #f8f9fc !important;
}

.hover-action-btn {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #ffffff;
  border-top: 6px solid #fb8c00 !important;
  cursor: pointer;
}
.hover-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.28) !important;
  border-color: rgba(251, 140, 0, 0.4) !important;
}

.clickable-card {
  cursor: pointer;
}
 .clickable-card:hover {
  transform: translateY(-5px);
  /* box-shadow:  */
    /* inset 6px 2px 0 0 var(--theme-color),  */
    /* inset -6px -2px 0 0 #9e9e9e, */
    /* 0 10px 20px rgba(0,0,0,0.08) !important; */
}  

.modern-search >>> .v-input__slot {
  border-radius: 12px !important;
  background-color: #F3F0F0 !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02) !important;
}
.modern-search >>> fieldset {
  border-color: #f0f2f5 !important;
}
.modern-search:hover >>> fieldset,
.modern-search.v-input--is-focused >>> fieldset {
  border-color: #fb8c00 !important;
}

.modern-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.modern-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.modern-scrollbar::-webkit-scrollbar-thumb {
  scrollbar-width: thin;
  scrollbar-color: #9e9e9e #f1f1f1;
  border-radius: 10px;
}
.modern-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}

.bg-white { background-color: #ffffff !important; }
.border-bottom { border-bottom: 1px solid #f0f0f0; }
.relative-col { position: relative; }
.sticky-preview {
  position: sticky;
  top: 16px;
  height: max-content; 
  z-index: 10;
  border: 1px solid rgba(0,0,0,0.05);
}
.position-relative { position: relative; }
.sticky-actions {
position: -webkit-sticky;
  position: sticky;
  bottom: 0 !important; 
  background-color: #ffffff; 
  z-index: 20;
  padding: 16px !important; 
  border-top: 1px solid #e0e0e0; 
  margin-bottom: 0 !important;
  width: 100%;}
.btn-save {
  background-color: #26a69a !important; 
  transition: all 0.2s;
}
.btn-save:hover { background-color: #00897b !important; }
.btn-cancel {
  color: #757575 !important;
  background-color: #f5f5f5 !important;
}
.btn-cancel:hover {
  background-color: #eeeeee !important;
  color: #d32f2f !important;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
.modern-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.modern-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1; 
  border-radius: 10px;
}

.modern-scrollbar::-webkit-scrollbar-thumb {
  background: #9e9e9e; 
  border-radius: 10px;
  border: 2px solid #f1f1f1; 
}

.modern-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #757575; 
}
@media (max-width: 599px) {
  .v-dialog.v-dialog--fullscreen {
    height: calc(100% - 60px) !important;
    bottom: 60px !important;
    top: 0 !important;
  }
}
</style>
